import { ConfirmationModalFooter } from '@/components/ConfirmationModal/Base/Footer';
import { useBulkDeleteInvoiceReport } from '@/features/invoice_reports/components/ConfirmDeleteModal/hooks/useBulkDeleteInvoiceReport';
import { useTranslation } from '@/i18n';
import {
  ButtonV2Props,
  ModalWithoutPortal,
} from '@moneyforward/ap-frontend-components';
import { FC, memo, useCallback, useMemo } from 'react';
import { useConfirmDeleteModal } from './context/ConfirmDeleteModalContext';

export type ConfirmBulkDeleteModalProps = {
  bulkDeleteModalOpen: boolean;
  bulkDeleteCount: number;
  bulkDeleteIds: string[];
  onCloseBulkDeleteModal: () => void;
  selectedId?: string | null;
  onDrawerClose?: () => void;
};

export const ConfirmBulkDeleteModal: FC<ConfirmBulkDeleteModalProps> = memo(
  ({
    bulkDeleteModalOpen,
    bulkDeleteCount,
    bulkDeleteIds,
    onCloseBulkDeleteModal,
    selectedId,
    onDrawerClose,
  }) => {
    const { refetch } = useConfirmDeleteModal();
    const { t } = useTranslation();

    const { bulkDeleteReports } = useBulkDeleteInvoiceReport({
      refetch,
      onCloseBulkDeleteModal,
      selectedId,
      onDrawerClose,
    });

    const handleDelete = useCallback(() => {
      return bulkDeleteReports(bulkDeleteIds)();
    }, [bulkDeleteReports, bulkDeleteIds]);

    const modalTitle = useMemo(() => {
      return t('invoice_reports.bulk_delete_confirm_title', {
        count: bulkDeleteCount,
      });
    }, [bulkDeleteCount, t]);

    const modalContent = useMemo(() => {
      return t('invoice_reports.bulk_delete_confirm_message', {
        count: bulkDeleteCount,
      });
    }, [bulkDeleteCount, t]);

    const cancelButtonProps: ButtonV2Props = useMemo(
      () => ({
        isTertiary: true,
        onClick: onCloseBulkDeleteModal,
        size: 'sm',
        children: t('cancel'),
      }),
      [onCloseBulkDeleteModal, t]
    );

    const deleteButtonProps: ButtonV2Props = useMemo(
      () => ({
        color: 'danger',
        isSecondary: true,
        onClick: handleDelete,
        size: 'sm',
        children: t('delete'),
      }),
      [handleDelete, t]
    );

    return (
      <ModalWithoutPortal
        open={bulkDeleteModalOpen}
        onClose={onCloseBulkDeleteModal}
        size='sm'
        title={modalTitle}
        mask
        footer={
          <ConfirmationModalFooter
            cancelButton={cancelButtonProps}
            okButton={deleteButtonProps}
          />
        }
      >
        <div>{modalContent}</div>
      </ModalWithoutPortal>
    );
  }
);

ConfirmBulkDeleteModal.displayName = 'ConfirmBulkDeleteModal';
